/**
 * ExtensionDetailView - full-width detail page for a selected editor extension.
 */

import type {
    BackendManifest,
    ExtensionManifest,
    ExtensionSourceInfo,
} from "@magic-md/extension-sdk";
import { useT } from "@magic-md/i18n/react";
import {
    ArrowLeft,
    Blocks,
    BookOpen,
    Code2,
    ExternalLink,
    ListChecks,
    MousePointer2,
    PanelLeftOpen,
    Puzzle,
    Server,
    Settings2,
    Shield,
    Zap,
} from "lucide-react";
import { useMemo, useSyncExternalStore, type ReactNode } from "react";
import { isExtensionActive } from "../extensions/extension-host";
import { useExtensionHostStore } from "../extensions/extension-host-store";
import { extensionUIRegistry } from "../extensions/extension-ui-registry";
import { contributionRegistry } from "../extensions/contribution-registry";
import {
    useExtensionSettingsPanel,
    useExtensionSidebarPanels,
} from "../extensions/useExtensionContributions";
import { cn } from "../lib/utils";
import { useEditorStore } from "../store/editor-store";
import { getThemeAccentStyle } from "../theme/runtime/theme-accents";

export function ExtensionDetailView({ extensionId }: { extensionId: string }) {
    const t = useT("editor");
    const setSelectedExtensionId = useEditorStore((s) => s.setSelectedExtensionId);
    const extension = useExtensionUI(extensionId);
    const panels = useExtensionSidebarPanels().filter(
        (panel) => panel.extensionId === extensionId,
    );
    const SettingsPanel = useExtensionSettingsPanel(extensionId);
    const active = isExtensionActive(extensionId);
    const contributionRevision = useContributionRevision();
    const commands = useMemo(() => {
        void contributionRevision;
        return contributionRegistry
            .commands()
            .filter((entry) => entry.extensionId === extensionId);
    }, [contributionRevision, extensionId]);

    const manifest = extension?.manifest ?? null;
    const source = extension?.source;
    const sourceRows = useMemo(
        () => (manifest ? buildSourceRows(manifest, source, t) : []),
        [manifest, source, t],
    );

    if (!manifest) {
        return (
            <div className="flex h-full flex-col items-center justify-center gap-3">
                <Puzzle size={32} className="text-muted-foreground/30" />
                <p className="text-sm text-muted-foreground">
                    {t("extensionDetail.notFound", { id: extensionId })}
                </p>
                <button
                    type="button"
                    className="text-xs text-primary hover:underline"
                    onClick={() => setSelectedExtensionId(null)}
                >
                    {t("pluginDetail.action.goBack")}
                </button>
            </div>
        );
    }

    const settingsFieldCount = countSchemaProperties(manifest.settingsSchema);
    const contributionGroups = buildContributionGroups(
        extensionId,
        manifest,
        Boolean(SettingsPanel),
        contributionRevision,
        t,
    );
    const contributionCount = contributionGroups.reduce(
        (sum, group) => sum + group.items.length,
        0,
    );

    return (
        <div className="flex h-full flex-col bg-background">
            <div className="flex h-10 shrink-0 items-center gap-2 border-b border-border bg-muted/20 px-4">
                <button
                    type="button"
                    className="flex items-center gap-1.5 text-xs text-muted-foreground transition-colors hover:text-foreground"
                    onClick={() => setSelectedExtensionId(null)}
                >
                    <ArrowLeft size={14} />
                    <span>{t("common.back")}</span>
                </button>
                <div className="h-4 w-px bg-border" />
                <span className="text-xs text-muted-foreground">
                    {t("extensionDetail.detailTitle")}
                </span>
            </div>

            <div className="min-h-0 flex-1 overflow-auto">
                <div className="mx-auto max-w-6xl space-y-6 px-6 py-8">
                    <HeaderSection
                        manifest={manifest}
                        active={active}
                    />

                    <Section
                        title={t("extensionDetail.sourceInfo")}
                        icon={<Server size={14} />}
                        boxed
                    >
                        <DetailRows rows={sourceRows} />
                    </Section>

                    {manifest.overview ? (
                        <Section
                            title={t("extensionDetail.overview")}
                            icon={<BookOpen size={14} />}
                            boxed
                        >
                            <p className="text-sm leading-relaxed text-muted-foreground">
                                {manifest.overview}
                            </p>
                        </Section>
                    ) : null}

                    {commands.length > 0 ? (
                        <Section
                            title={t("extensionDetail.contributions.commands")}
                            icon={<MousePointer2 size={14} />}
                            count={commands.length}
                        >
                            <div className="grid gap-2 sm:grid-cols-2">
                                {commands.map(({ command }) => (
                                    <button
                                        key={command.id}
                                        type="button"
                                        className="rounded-lg border border-border bg-card p-3 text-left transition-colors hover:border-primary/30 hover:bg-primary/5"
                                        onClick={() => {
                                            void command.handler();
                                        }}
                                    >
                                        <div className="truncate text-sm font-medium text-foreground">
                                            {command.title}
                                        </div>
                                        <p className="mt-1 font-mono text-[11px] text-muted-foreground">
                                            {command.id}
                                        </p>
                                    </button>
                                ))}
                            </div>
                        </Section>
                    ) : null}

                    <Section
                        title={t("extensionDetail.runtime")}
                        icon={<Zap size={14} />}
                        boxed
                    >
                        <DetailRows
                            rows={[
                                {
                                    label: t("extensionDetail.runtime.type"),
                                    value: manifest.runtime.type,
                                },
                                {
                                    label: t("extensionDetail.runtime.status"),
                                    value: active
                                        ? t("extensionDetail.status.active")
                                        : t("extensionDetail.status.inactive"),
                                },
                                {
                                    label: t("extensionDetail.activationEvents"),
                                    value: formatList(manifest.activationEvents),
                                },
                            ]}
                        />
                    </Section>

                    {panels.length > 0 ? (
                        <Section
                            title={t("extensionDetail.sidebarPanels")}
                            icon={<PanelLeftOpen size={14} />}
                            count={panels.length}
                        >
                            <div className="grid gap-2 sm:grid-cols-2">
                                {panels.map(({ panel }) => (
                                    <button
                                        key={panel.id}
                                        type="button"
                                        className="rounded-lg border border-border bg-card p-3 text-left transition-colors hover:border-primary/30 hover:bg-primary/5"
                                        onClick={() => {
                                            setSelectedExtensionId(null);
                                            useExtensionHostStore
                                                .getState()
                                                .focusPanel(panel.id);
                                        }}
                                    >
                                        <div className="flex items-center gap-2">
                                            <span className="text-sm">
                                                {panel.icon ?? <Blocks size={14} />}
                                            </span>
                                            <span className="min-w-0 truncate text-sm font-medium text-foreground">
                                                {panel.title}
                                            </span>
                                        </div>
                                        <p className="mt-1 font-mono text-[11px] text-muted-foreground">
                                            {panel.id}
                                        </p>
                                    </button>
                                ))}
                            </div>
                        </Section>
                    ) : null}

                    {(manifest.features?.length ?? 0) > 0 ? (
                        <Section
                            title={t("extensionDetail.features")}
                            icon={<Blocks size={14} />}
                            count={manifest.features?.length}
                        >
                            <PillList items={manifest.features ?? []} />
                        </Section>
                    ) : null}

                    {(manifest.permissions?.length ?? 0) > 0 ? (
                        <Section
                            title={t("extensionDetail.permissions")}
                            icon={<Shield size={14} />}
                            count={manifest.permissions?.length}
                        >
                            <PillList items={manifest.permissions ?? []} mono />
                        </Section>
                    ) : null}

                    {(manifest.backends?.length ?? 0) > 0 ? (
                        <Section
                            title={t("extensionDetail.backends")}
                            icon={<Server size={14} />}
                            count={manifest.backends?.length}
                        >
                            <BackendList backends={manifest.backends ?? []} />
                        </Section>
                    ) : null}

                    {(settingsFieldCount > 0 || SettingsPanel) ? (
                        <Section
                            title={t("extensionDetail.settings")}
                            icon={<Settings2 size={14} />}
                            boxed
                        >
                            <DetailRows
                                rows={[
                                    {
                                        label: t("extensionDetail.settings.fields"),
                                        value: String(settingsFieldCount),
                                    },
                                    {
                                        label: t("extensionDetail.settings.customPanel"),
                                        value: SettingsPanel
                                            ? t("common.yes")
                                            : t("common.no"),
                                    },
                                ]}
                            />
                        </Section>
                    ) : null}

                    {contributionCount > 0 ? (
                        <Section
                            title={t("extensionDetail.contributions")}
                            icon={<ListChecks size={14} />}
                            count={contributionCount}
                        >
                            <ContributionGroups groups={contributionGroups} />
                        </Section>
                    ) : null}

                    <Section
                        title={t("extensionDetail.manifest")}
                        icon={<Code2 size={14} />}
                    >
                        <pre className="max-h-[360px] overflow-auto rounded-lg border border-border bg-card p-4 text-xs text-muted-foreground">
                            {JSON.stringify(manifest, null, 2)}
                        </pre>
                    </Section>
                </div>
            </div>
        </div>
    );
}

function useContributionRevision(): number {
    return useSyncExternalStore(
        (onChange) => contributionRegistry.subscribe(onChange),
        () => contributionRegistry.revision(),
        () => contributionRegistry.revision(),
    );
}

function useExtensionUI(extensionId: string) {
    const revision = useSyncExternalStore(
        (onChange) => extensionUIRegistry.subscribe(onChange),
        () => extensionUIRegistry.revision(),
        () => extensionUIRegistry.revision(),
    );
    return useMemo(() => {
        void revision;
        return extensionUIRegistry.get(extensionId);
    }, [extensionId, revision]);
}

function HeaderSection({
    manifest,
    active,
}: {
    manifest: ExtensionManifest;
    active: boolean;
}) {
    const t = useT("editor");
    return (
        <div className="flex flex-col gap-4 rounded-xl border border-border bg-card p-5 sm:flex-row sm:items-start sm:justify-between">
            <div className="flex min-w-0 gap-4">
                <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-xl border border-border bg-background text-2xl">
                    {manifest.icon ?? <Puzzle size={24} />}
                </div>
                <div className="min-w-0">
                    <div className="mb-1 flex flex-wrap items-center gap-2">
                        <h1 className="truncate text-2xl font-semibold text-foreground">
                            {manifest.title ?? manifest.name}
                        </h1>
                        <span
                            className={cn(
                                "rounded-full px-2 py-0.5 text-[11px] font-medium",
                                active
                                    ? "theme-accent-chip"
                                    : "bg-muted text-muted-foreground",
                            )}
                            style={active ? getThemeAccentStyle("success") : undefined}
                        >
                            {active
                                ? t("extensionDetail.status.active")
                                : t("extensionDetail.status.inactive")}
                        </span>
                    </div>
                    <p className="font-mono text-xs text-muted-foreground">
                        {manifest.id}
                    </p>
                    <p className="mt-2 max-w-3xl text-sm leading-relaxed text-muted-foreground">
                        {manifest.description}
                    </p>
                    <div className="mt-3 flex flex-wrap gap-2">
                        <DetailPill label={`v${manifest.version}`} />
                        <DetailPill label={manifest.runtime.type} mono />
                        {manifest.category ? (
                            <DetailPill label={manifest.category} />
                        ) : null}
                    </div>
                </div>
            </div>
            <div className="flex shrink-0 flex-wrap gap-2">
                {manifest.homepage ? (
                    <a
                        href={manifest.homepage}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-1.5 rounded-md border border-border px-3 py-1.5 text-xs text-muted-foreground transition-colors hover:text-foreground"
                    >
                        <ExternalLink size={13} />
                        {t("extensionDetail.homepage")}
                    </a>
                ) : null}
            </div>
        </div>
    );
}

function Section({
    title,
    icon,
    count,
    boxed = false,
    children,
}: {
    title: string;
    icon: ReactNode;
    count?: number;
    boxed?: boolean;
    children: ReactNode;
}) {
    return (
        <section className="space-y-3">
            <div className="flex items-center gap-2">
                <span className="text-muted-foreground">{icon}</span>
                <h2 className="text-sm font-semibold text-foreground">{title}</h2>
                {count != null ? (
                    <span className="rounded-full bg-muted px-1.5 py-0.5 text-[10px] text-muted-foreground">
                        {count}
                    </span>
                ) : null}
            </div>
            <div className={boxed ? "rounded-lg border border-border bg-card p-4" : ""}>
                {children}
            </div>
        </section>
    );
}

interface DetailRow {
    label: string;
    value?: string;
    href?: string;
    mono?: boolean;
}

function DetailRows({ rows }: { rows: DetailRow[] }) {
    const visibleRows = rows.filter((row) => row.value);
    if (visibleRows.length === 0) {
        return null;
    }
    return (
        <dl className="grid gap-3 sm:grid-cols-2">
            {visibleRows.map((row) => (
                <div key={row.label} className="min-w-0">
                    <dt className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground/70">
                        {row.label}
                    </dt>
                    <dd
                        className={cn(
                            "mt-1 break-words text-sm text-foreground",
                            row.mono && "font-mono text-xs",
                        )}
                    >
                        {row.href ? (
                            <a
                                href={row.href}
                                target="_blank"
                                rel="noreferrer"
                                className="text-primary hover:underline"
                            >
                                {row.value}
                            </a>
                        ) : (
                            row.value
                        )}
                    </dd>
                </div>
            ))}
        </dl>
    );
}

function DetailPill({ label, mono = false }: { label: string; mono?: boolean }) {
    return (
        <span
            className={cn(
                "rounded-full border border-border bg-background px-2 py-0.5 text-[11px] text-muted-foreground",
                mono && "font-mono",
            )}
        >
            {label}
        </span>
    );
}

function PillList({ items, mono = false }: { items: string[]; mono?: boolean }) {
    return (
        <div className="flex flex-wrap gap-2">
            {items.map((item) => (
                <DetailPill key={item} label={item} mono={mono} />
            ))}
        </div>
    );
}

function BackendList({ backends }: { backends: BackendManifest[] }) {
    return (
        <div className="grid gap-2 sm:grid-cols-2">
            {backends.map((backend) => (
                <div
                    key={backend.id}
                    className="rounded-lg border border-border bg-card p-3"
                >
                    <div className="font-mono text-sm text-foreground">
                        {backend.id}
                    </div>
                    {backend.command ? (
                        <div className="mt-1 font-mono text-xs text-muted-foreground">
                            {backend.command}
                        </div>
                    ) : null}
                    {backend.capabilities?.length ? (
                        <div className="mt-2 flex flex-wrap gap-1">
                            {backend.capabilities.map((capability) => (
                                <DetailPill
                                    key={capability}
                                    label={capability}
                                    mono
                                />
                            ))}
                        </div>
                    ) : null}
                </div>
            ))}
        </div>
    );
}

interface ContributionGroup {
    label: string;
    items: string[];
    mono?: boolean;
}

function ContributionGroups({ groups }: { groups: ContributionGroup[] }) {
    return (
        <div className="grid gap-3 md:grid-cols-2">
            {groups
                .filter((group) => group.items.length > 0)
                .map((group) => (
                    <div
                        key={group.label}
                        className="rounded-lg border border-border bg-card p-3"
                    >
                        <div className="mb-2 flex items-center gap-2 text-xs font-semibold text-foreground">
                            <MousePointer2 size={13} className="text-muted-foreground" />
                            {group.label}
                            <span className="rounded-full bg-muted px-1.5 py-0.5 text-[10px] text-muted-foreground">
                                {group.items.length}
                            </span>
                        </div>
                        <div className="flex flex-wrap gap-1.5">
                            {group.items.map((item) => (
                                <DetailPill key={item} label={item} mono={group.mono} />
                            ))}
                        </div>
                    </div>
                ))}
        </div>
    );
}

function buildSourceRows(
    manifest: ExtensionManifest,
    source?: ExtensionSourceInfo,
    t?: (key: string) => string,
): DetailRow[] {
    const distributionKind = source?.distributionKind ?? "local";
    const label = (key: string, fallback: string) => t?.(key) ?? fallback;
    return [
        {
            label: label("extensionDetail.source.type", "Type"),
            value: distributionKind,
        },
        {
            label: label("extensionDetail.source.version", "Version"),
            value: source?.version ?? manifest.version,
        },
        {
            label: label("extensionDetail.source.manifestUrl", "Manifest URL"),
            value: source?.manifestUrl,
            href: source?.manifestUrl,
            mono: true,
        },
        {
            label: label("extensionDetail.source.uiBundleUrl", "UI Bundle URL"),
            value: source?.uiBundleUrl,
            href: source?.uiBundleUrl,
            mono: true,
        },
        {
            label: label("extensionDetail.source.cssUrl", "CSS URL"),
            value: source?.cssUrl,
            href: source?.cssUrl,
            mono: true,
        },
        {
            label: label("extensionDetail.source.invokeUrl", "Invoke URL"),
            value: source?.invokeUrl,
            href: source?.invokeUrl,
            mono: true,
        },
        {
            label: label("extensionDetail.source.localPath", "Local Path"),
            value: source?.localPath,
            mono: true,
        },
        {
            label: label("extensionDetail.source.manifestPath", "Manifest Path"),
            value: source?.manifestPath,
            mono: true,
        },
        {
            label: label("extensionDetail.source.uiBundlePath", "UI Bundle Path"),
            value: source?.uiBundlePath,
            mono: true,
        },
    ];
}

function countSchemaProperties(schema: ExtensionManifest["settingsSchema"]): number {
    const properties = schema?.properties;
    return properties ? Object.keys(properties).length : 0;
}

function formatList(values: string[] | undefined): string | undefined {
    return values && values.length > 0 ? values.join(", ") : undefined;
}

function buildContributionGroups(
    extensionId: string,
    manifest: ExtensionManifest,
    hasSettingsPanel: boolean,
    revision: number,
    t: (key: string) => string,
): ContributionGroup[] {
    void revision;
    const contract = manifest.contract as
        | {
            methods?: string[];
            events?: string[];
            exports?: string[];
        }
        | undefined;
    const commands = contributionRegistry
        .commands()
        .filter((entry) => entry.extensionId === extensionId)
        .map(({ command }) => `${command.id} · ${command.title}`);
    const statusBar = contributionRegistry
        .statusBarItems()
        .filter((entry) => entry.extensionId === extensionId)
        .map(({ item }) => `${item.id} · ${item.align ?? "left"}`);
    const menuProviders = contributionRegistry
        .menuProviders()
        .filter((entry) => entry.extensionId === extensionId)
        .map(({ provider }) => provider.surface);
    const unitActions = contributionRegistry
        .unitConfigActions()
        .filter((entry) => entry.extensionId === extensionId)
        .map(({ action }) => `${action.id} · ${action.label}`);

    return [
        {
            label: t("extensionDetail.contributions.commands"),
            items: commands,
            mono: true,
        },
        {
            label: t("extensionDetail.contributions.statusBar"),
            items: statusBar,
            mono: true,
        },
        {
            label: t("extensionDetail.contributions.contextMenus"),
            items: [...new Set(menuProviders)],
            mono: true,
        },
        {
            label: t("extensionDetail.contributions.unitActions"),
            items: unitActions,
            mono: true,
        },
        {
            label: t("extensionDetail.contributions.settings"),
            items: [
                ...(hasSettingsPanel ? ["custom-panel"] : []),
                ...Object.keys(manifest.settingsSchema?.properties ?? {}).map(
                    (key) => `schema.${key}`,
                ),
            ],
            mono: true,
        },
        {
            label: t("extensionDetail.contributions.runtimeMethods"),
            items: contract?.methods ?? [],
            mono: true,
        },
        {
            label: t("extensionDetail.contributions.runtimeEvents"),
            items: contract?.events ?? [],
            mono: true,
        },
        {
            label: t("extensionDetail.contributions.exports"),
            items: contract?.exports ?? [],
            mono: true,
        },
    ];
}
